/**
 * Utility functions for managing search parameters in localStorage
 * Each dashboard/question is tracked separately by ID
 */

const STORAGE_KEY_PREFIX = 'analytics-search-params-';

/**
 * Extract dashboard or question ID from a Metabase URL path
 * @param {string} pathname - The pathname from Metabase location
 * @returns {string|null} - The ID or null if not found
 */
export const extractIdFromPath = (pathname) => {
  if (!pathname) return null;
  
  // Handle dashboard URLs: /dashboard/123-name or /dashboard/123
  if (pathname.startsWith('/dashboard/')) {
    const parts = pathname.split('/');
    if (parts.length >= 3) {
      const idPart = parts[2];
      // Extract just the numeric ID (before any dash)
      return idPart.split('-')[0];
    }
  }
  
  // Handle question URLs: /question/123-name or /question/123
  if (pathname.startsWith('/question/')) {
    const parts = pathname.split('/');
    if (parts.length >= 3) {
      const idPart = parts[2];
      // Extract just the numeric ID (before any dash)
      return idPart.split('-')[0];
    }
  }
  
  // Handle other potential patterns
  // Add more patterns as needed
  
  return null;
};

/**
 * Get the storage key for a specific dashboard/question ID
 * @param {string} id - The dashboard/question ID
 * @returns {string} - The localStorage key
 */
const getStorageKey = (id) => {
  return `${STORAGE_KEY_PREFIX}${id}`;
};

/**
 * Save search parameters for a specific dashboard/question
 * @param {string} id - The dashboard/question ID
 * @param {string} searchParams - The search parameters string (e.g., "?param1=value1&param2=value2")
 */
export const saveSearchParams = (id, searchParams) => {
  if (!id) {
    console.warn('Cannot save search params: no ID provided');
    return;
  }
  
  try {
    const key = getStorageKey(id);
    localStorage.setItem(key, searchParams);
    
    if (import.meta.env.DEV) {
      console.log(`💾 Saved search params for ${id}:`, searchParams);
    }
  } catch (error) {
    console.error('Failed to save search params to localStorage:', error);
  }
};

/**
 * Load search parameters for a specific dashboard/question
 * @param {string} id - The dashboard/question ID
 * @returns {string|null} - The saved search parameters or null if not found
 */
export const loadSearchParams = (id) => {
  if (!id) {
    console.warn('Cannot load search params: no ID provided');
    return null;
  }
  
  try {
    const key = getStorageKey(id);
    const savedParams = localStorage.getItem(key);
    
    if (import.meta.env.DEV && savedParams) {
      console.log(`📖 Loaded search params for ${id}:`, savedParams);
    }
    
    return savedParams;
  } catch (error) {
    console.error('Failed to load search params from localStorage:', error);
    return null;
  }
};

/**
 * Clear search parameters for a specific dashboard/question
 * @param {string} id - The dashboard/question ID
 */
export const clearSearchParams = (id) => {
  if (!id) {
    console.warn('Cannot clear search params: no ID provided');
    return;
  }
  
  try {
    const key = getStorageKey(id);
    localStorage.removeItem(key);
    
    if (import.meta.env.DEV) {
      console.log(`🗑️ Cleared search params for ${id}`);
    }
  } catch (error) {
    console.error('Failed to clear search params from localStorage:', error);
  }
};

/**
 * Get all saved search parameter keys (for debugging/management)
 * @returns {string[]} - Array of all saved keys
 */
export const getAllSavedKeys = () => {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    return keys;
  } catch (error) {
    console.error('Failed to get saved keys from localStorage:', error);
    return [];
  }
};

/**
 * Clear all saved search parameters (for cleanup)
 */
export const clearAllSearchParams = () => {
  try {
    const keys = getAllSavedKeys();
    keys.forEach(key => localStorage.removeItem(key));
    
    if (import.meta.env.DEV) {
      console.log(`🗑️ Cleared all search params (${keys.length} items)`);
    }
  } catch (error) {
    console.error('Failed to clear all search params from localStorage:', error);
  }
};
